import cx from 'classnames';
import { Location } from 'history';
import { AdSlot } from 'mk2/containers/AdSlot/AdSlot';
import { BrandingProps } from 'mk2/pages/Page';
import React, { useEffect, useRef, useState } from 'react';
import styles from './AdSlotBranding.mscss';

export const PREFIX_AD_SLOT_BRANDING = 'mk-branding';

interface OwnProps extends BrandingProps {
    location: Location;
    isMobile: boolean;
}

type Props = OwnProps;

/**
 * Determines the final ad zone name with device suffix for backward compatibility.
 *
 * Supports both old and new naming conventions:
 * - Old style: " (Mobile)" / " (Desktop)" - e.g., "Market Section - Branding (Mobile)"
 * - New style: " - Mobile" / " - Desktop" - e.g., "Market Section - Top 01 - Mobile"
 *
 * Behavior:
 * 1. If zone already ends with any of these 4 suffixes → use as-is (new explicit naming)
 * 2. Otherwise → append " (Mobile)" or " (Desktop)" based on isMobile (backward compatibility)
 *
 * Examples:
 * - Input: "Market Section - Top 01 - Mobile" → Output: "Market Section - Top 01 - Mobile" (unchanged)
 * - Input: "Market Section - Branding" + isMobile=true → Output: "Market Section - Branding (Mobile)" (suffix added)
 * - Input: "Market Section - Branding (Desktop)" → Output: "Market Section - Branding (Desktop)" (unchanged)
 *
 * @param zone - Base zone name or zone name with explicit suffix
 * @param isMobile - Whether the current device is mobile
 * @returns Final zone name with appropriate device suffix
 */
const getZoneWithDeviceSuffix = (zone: string, isMobile: boolean): string => {
    // Check if zone already has any of the supported device suffixes
    const hasExplicitSuffix = zone.endsWith(' (Mobile)')
        || zone.endsWith(' (Desktop)')
        || zone.endsWith(' - Mobile')
        || zone.endsWith(' - Desktop');

    // If zone already has a suffix, use it as-is (new explicit naming)
    if (hasExplicitSuffix) {
        return zone;
    }

    // Otherwise, append old-style suffix for backward compatibility
    return isMobile ? `${zone} (Mobile)` : `${zone} (Desktop)`;
};

export const AdSlotBranding: React.FC<Props> = ({
    zone, targeting, location, isMobile,
}) => {
    const finalZone = getZoneWithDeviceSuffix(zone, isMobile);

    return (
        <React.Fragment>
            <div
                className={cx(!isMobile && styles.AdSlotBranding__desktopWrap)}
            >
                <AdSlot
                    className={isMobile ? styles.AdSlotBranding__mobile : styles.AdSlotBranding__desktop}
                    zone={finalZone}
                    location={location}
                    targeting={targeting}
                    prefix={PREFIX_AD_SLOT_BRANDING}
                    marginBottomWithAd={isMobile ? 3 : undefined}
                    marginTopWithAd={isMobile ? 3 : undefined}
                    allowDownscaling={isMobile}
                />
            </div>
        </React.Fragment>
    );
};
